/**
 * NeedsAttentionWidget - Виджет "Требуют внимания"
 *
 * Показывает организации и задачи, требующие внимания:
 * - Организации без активности
 * - Пропущенные звонки
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Stack,
  Group,
  Text,
  Title,
  Box,
  Loader,
  Center,
  Badge,
  Divider,
  ThemeIcon,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconBuilding,
  IconPhone,
  IconClock,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import tokens from '../../theme/designTokens';

const OrganizationItem = ({ org, onClick }) => {
  return (
    <Group
      p="xs"
      style={{
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[2])}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      onClick={onClick}
    >
      <ThemeIcon size="sm" radius="xl" color="orange" variant="light">
        <IconBuilding size={12} />
      </ThemeIcon>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="xs" fw={500} truncate>
          {org.name}
        </Text>
        <Text size="xs" c="dimmed">
          {org.days_inactive} дней без активности
        </Text>
      </Box>
    </Group>
  );
};

const MissedCallItem = ({ call, onClick }) => {
  return (
    <Group
      p="xs"
      style={{
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background-color 150ms ease',
      }}
      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = tokens.colors.neutral[2])}
      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
      onClick={onClick}
    >
      <ThemeIcon size="sm" radius="xl" color="red" variant="light">
        <IconPhone size={12} />
      </ThemeIcon>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="xs" fw={500} truncate>
          {call.organization_name}
        </Text>
        {call.contact_name && (
          <Text size="xs" c="dimmed" truncate>
            {call.contact_name}
          </Text>
        )}
      </Box>
    </Group>
  );
};

const NeedsAttentionWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/dashboard/sales/needs-attention', {
        credentials: 'include',
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (err) {
      console.error('Failed to load needs attention:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOrgClick = (org) => {
    navigate(`/organizations/${org.id}`);
  };

  const handleCallClick = (call) => {
    navigate(`/organizations/${call.organization_id}`);
  };

  const totalItems = data
    ? (data.no_activity?.length || 0) + (data.overdue_calls?.length || 0)
    : 0;

  return (
    <Card shadow="sm" radius={16} withBorder p="lg" h="100%">
      <Stack gap="md">
        {/* Заголовок */}
        <Group justify="space-between">
          <Group gap="xs">
            <IconAlertTriangle size={20} style={{ color: tokens.colors.semantic.warning.primary }} />
            <Title order={4}>Требуют внимания</Title>
          </Group>
          {totalItems > 0 && (
            <Badge color="orange" variant="filled" radius="xl" size="sm">
              {totalItems}
            </Badge>
          )}
        </Group>

        {/* Контент */}
        {loading ? (
          <Center py="xl">
            <Loader size="sm" />
          </Center>
        ) : totalItems === 0 ? (
          <Center py="xl">
            <Stack align="center" gap="xs">
              <IconClock size={40} style={{ color: tokens.colors.neutral[4] }} />
              <Text size="sm" c="dimmed" ta="center">
                Всё под контролем!
              </Text>
            </Stack>
          </Center>
        ) : (
          <Stack gap="xs">
            {/* Организации без активности */}
            {data.no_activity?.length > 0 && (
              <>
                <Text size="xs" fw={500} c="dimmed" tt="uppercase">
                  Без активности
                </Text>
                {data.no_activity.map((org) => (
                  <OrganizationItem
                    key={org.id}
                    org={org}
                    onClick={() => handleOrgClick(org)}
                  />
                ))}
              </>
            )}

            {/* Разделитель */}
            {data.no_activity?.length > 0 && data.overdue_calls?.length > 0 && (
              <Divider />
            )}

            {/* Пропущенные звонки */}
            {data.overdue_calls?.length > 0 && (
              <>
                <Text size="xs" fw={500} c="dimmed" tt="uppercase">
                  Пропущенные звонки
                </Text>
                {data.overdue_calls.map((call) => (
                  <MissedCallItem
                    key={call.id}
                    call={call}
                    onClick={() => handleCallClick(call)}
                  />
                ))}
              </>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};

export default NeedsAttentionWidget;
